// KEZDŐLAP — a KITŰZÖTT közlemények (T15). Nem az automatikus „legutóbbi üzenet" (azt egy korábbi user-döntés
// kivezette, mert a harang már szól a friss üzenetről): itt csak az jelenik meg, amit egy kezelő SZÁNDÉKOSAN
// kitűzött, és CSAK ha NEKEM SZÓL (mindenkinek · a csapatomnak / részlegemnek · személyesen nekem — a BFF szűr).
// Legfeljebb `PINNED_LIMIT` sor, a legutóbb kitűzött elöl; ha több van, „+N további kitűzött" link a kitűzött-szűrős
// üzenetlistára. A sor az üzenet részletére visz (`/messages?open=<id>`, az irány-szűrő „mind"-re állítva — a
// mindenkinek szóló saját kitűzésem KIMENŐ, a bejövő alap-nézetben nem találná a mélylink).
import { BookmarkIcon, EnvelopeIcon } from '@heroicons/react/20/solid';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { PanelCard } from '@/components/shared/panel-card';
import { PersonChip } from '@/components/shared/person-chip';
import { Text } from '@/components/ui/text';
import type { DashboardPinnedMessage } from '@/features/dashboard/use-dashboard-data';
import { formatDateTime, useTimeZone } from '@/lib/format-datetime';

/** A messages-lista „mind" irány-értéke (a route `ALL` konstansa). */
const DIRECTION_ALL = '__all__';

export interface PinnedMessageCardProps {
  messages: DashboardPinnedMessage[];
  /** Az összes nekem szóló kitűzött darabszáma — ha több a mutatottnál, „+N további" link. */
  total: number;
}

export function PinnedMessageCard({ messages, total }: PinnedMessageCardProps): JSX.Element {
  const { t } = useTranslation();
  const more = total - messages.length;
  return (
    <PanelCard title={t('dashboard.pinnedMessage.title')} loading={false}>
      <ul className="divide-border divide-y">
        {messages.map((message) => (
          <li key={message.id}>
            <PinnedMessageRow message={message} />
          </li>
        ))}
      </ul>
      {more > 0 && (
        <Link
          to="/messages"
          search={{ pinned: 'only', direction: DIRECTION_ALL } as never}
          className="text-primary hover:bg-accent/60 focus-visible:ring-ring block px-4 py-2 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset"
          data-testid="pinned-message-more"
        >
          {t('dashboard.pinnedMessage.more', { count: more })}
        </Link>
      )}
    </PanelCard>
  );
}

function PinnedMessageRow({ message }: { message: DashboardPinnedMessage }): JSX.Element {
  const { t, i18n } = useTranslation();
  const timeZone = useTimeZone();
  return (
    <Link
      to="/messages"
      search={{ open: message.id, direction: DIRECTION_ALL } as never}
      className="hover:bg-accent/60 focus-visible:ring-ring flex flex-col gap-1.5 px-4 py-3 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset"
      data-testid="pinned-message"
    >
      <span className="flex min-w-0 items-center gap-2">
        <BookmarkIcon className="text-primary size-4 shrink-0" aria-hidden />
        {!message.readByMe && (
          <span role="status" aria-label={t('messages.unread')} className="flex shrink-0">
            <EnvelopeIcon className="text-destructive size-4" aria-hidden />
          </span>
        )}
        <Text variant="itemTitle" as="span" className="min-w-0 truncate">
          {message.subject}
        </Text>
      </span>
      <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
        {message.senderName !== null && message.senderName !== '' && (
          <PersonChip
            name={message.senderName}
            givenName={message.senderGivenName}
            familyName={message.senderFamilyName}
            avatarUrl={message.senderAvatarUrl}
            accentColor={message.senderTeamColor}
            size="sm"
            className="gap-1.5 text-xs"
          />
        )}
        <Text variant="secondary" as="span" className="tabular-nums">
          {t('dashboard.pinnedMessage.pinnedAt', {
            date: formatDateTime(message.pinnedAt, i18n.language, timeZone, {
              dateStyle: 'medium',
            }),
          })}
        </Text>
      </span>
    </Link>
  );
}
